import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';

import { Banner } from '../../core/models/banner.model';
import { fuentesFoto } from '../../core/models/propiedad.model';

/**
 * Banner publicitario del portal público (BP-02 / FP-27, spec §17): imagen
 * desktop y mobile que sirve `GET /publico/banners`. Sin copy propio — el
 * arte de cada banner lo carga el anunciante desde el backoffice.
 *
 * `[banner]` es obligatorio a propósito: es el llamador quien decide si hay
 * banner para mostrar (el endpoint omite las secciones sin uno activo).
 */
@Component({
  selector: 'app-banner-cta',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgTemplateOutlet],
  template: `
    @if (banner().link; as link) {
      <a [href]="link" class="banner" target="_blank" rel="noopener">
        <ng-container [ngTemplateOutlet]="imagenes" />
      </a>
    } @else {
      <div class="banner">
        <ng-container [ngTemplateOutlet]="imagenes" />
      </div>
    }

    <ng-template #imagenes>
      <picture class="banner__foto banner__foto--desktop">
        @if (desktop().avif; as avif) {
          <source [srcset]="avif" type="image/avif" />
        }
        <img
          [src]="desktop().src"
          alt="Publicidad"
          loading="lazy"
          decoding="async"
        />
      </picture>
      <picture class="banner__foto banner__foto--mobile">
        @if (mobile().avif; as avif) {
          <source [srcset]="avif" type="image/avif" />
        }
        <img
          [src]="mobile().src"
          alt="Publicidad"
          loading="lazy"
          decoding="async"
        />
      </picture>
    </ng-template>
  `,
  styles: `
    .banner {
      display: block;
      border-radius: 4px;
      overflow: hidden;
    }

    .banner__foto {
      display: block;
      width: 100%;
    }

    .banner__foto img {
      display: block;
      width: 100%;
      height: auto;
    }

    .banner__foto--mobile {
      display: none;
    }

    @media (max-width: 700px) {
      .banner__foto--desktop {
        display: none;
      }

      .banner__foto--mobile {
        display: block;
      }
    }
  `,
})
export class BannerCta {
  readonly banner = input.required<Banner>();

  /** `full` (1920): el banner ocupa el ancho del contenido en desktop. */
  protected readonly desktop = computed(() =>
    fuentesFoto(this.banner().imagenDesktop, 'full'),
  );

  /** `card` (800): de sobra para el ancho de pantalla mobile (≤700 px). */
  protected readonly mobile = computed(() =>
    fuentesFoto(this.banner().imagenMobile, 'card'),
  );
}
